import { useTranslation } from "react-i18next";

import { cn } from "@bcc/lib/utils";
import { vatRateToPercent, type RegionPriceBreakdown } from "@bcc/lib/products/cross-border-tax";

/** Cross-border destinations (DE/NL) are EUR-only, so amounts render with a euro prefix. */
const formatEuro = (value: number): string => `€${value.toFixed(2)}`;

export type CrossBorderBreakdownPart = "net" | "ks" | "vat" | "total" | "note";

interface CrossBorderBreakdownProps {
  breakdown: RegionPriceBreakdown;
  /** VAT rate (fraction, e.g. 0.07) charged on this leg — drives the "VAT {{vat}}% on …" label. */
  vatRate: number;
  /** Which price leg this breakdown belongs to, picking the "Final RRP" vs "Final wholesale price" label. */
  field: "msrp" | "wholesalePrice";
  /** Builds a data-testid for each line; the caller owns the namespace/index so ids stay setter-driven. */
  getTestId: (part: CrossBorderBreakdownPart) => string;
  /** Optional footnote shown under the total (e.g. the BRG-lodges-KS wholesale note). */
  note?: string;
}

const ROW = "flex justify-between text-[11px]";

/**
 * The Netto / Kaffeesteuer / VAT / Final breakdown box shown beneath every
 * cross-border price input. VAT is charged on (Netto + KS); the Kaffeesteuer row
 * is rendered only when the destination actually levies it. Shared by the SKU
 * import form and the price-update popover so both screens stay in lockstep.
 */
export const CrossBorderBreakdown = ({ breakdown, vatRate, field, getTestId, note }: CrossBorderBreakdownProps) => {
  const { t } = useTranslation();
  const hasKs = breakdown.ks > 0;
  const vatLabel = t(hasKs ? "products.priceUpdate.crossBorder.vatOnNettoKs" : "products.priceUpdate.crossBorder.vatOnNetto", {
    vat: vatRateToPercent(vatRate),
  });
  const finalLabel = t(
    field === "msrp" ? "products.priceUpdate.crossBorder.finalRrp" : "products.priceUpdate.crossBorder.finalWholesalePrice"
  );

  return (
    <div className="mt-1.5 space-y-1 rounded-md border bg-muted/30 p-2">
      <div className={cn(ROW, "border-b border-dashed pb-1 text-muted-foreground")}>
        <span>{t("products.priceUpdate.crossBorder.nettoLine")}</span>
        <span className="font-medium text-foreground" data-testid={getTestId("net")}>
          {formatEuro(breakdown.net)}
        </span>
      </div>
      {hasKs && (
        <div className={cn(ROW, "text-red-600 dark:text-red-400")}>
          <span>{t("products.priceUpdate.crossBorder.ksLine")}</span>
          <span className="font-medium" data-testid={getTestId("ks")}>
            {formatEuro(breakdown.ks)}
          </span>
        </div>
      )}
      <div className={cn(ROW, "text-muted-foreground")}>
        <span>{vatLabel}</span>
        <span className="font-medium text-foreground" data-testid={getTestId("vat")}>
          {formatEuro(breakdown.vat)}
        </span>
      </div>
      <div className={cn(ROW, "border-t pt-1 text-[12px] font-semibold text-foreground")}>
        <span>{finalLabel}</span>
        <span data-testid={getTestId("total")}>{formatEuro(breakdown.total)}</span>
      </div>
      {note && (
        <p className="flex items-start gap-1 pt-1 text-[10px] text-muted-foreground" data-testid={getTestId("note")}>
          <span aria-hidden="true">ℹ️</span>
          <span>{note}</span>
        </p>
      )}
    </div>
  );
};
